import { useEffect, useRef, useState } from "react";
import maplibregl, { type Map as MlMap, type StyleSpecification } from "maplibre-gl";
import { METRO_LINE_COLORS, COUNTRY_GEOJSON_URL, type BBox, type GeometryKind } from "../lib/types";

const SOURCE_ID = "entities";
const COUNTRY_SOURCE_ID = "country";
const COUNTRY_FILL_LAYER = "country-fill";
const COUNTRY_OUTLINE_LAYER = "country-outline";
const FILL_LAYER = "ent-fill";
const OUTLINE_LAYER = "ent-outline";
const GUESSED_OUTLINE_LAYER = "ent-outline-guessed";
const POINT_LAYER = "ent-point";
const LINE_HIT_LAYER = "ent-line-hit";
const LINE_LAYER = "ent-line";

/** Default colour for un-guessed river lines (slate-400-ish). */
const LINE_DEFAULT = "#94a3b8";
/** Colour for guessed / revealed river lines (sky-600 – reads as water). */
const LINE_GUESSED = "#0284c7";

// Fill colours used by both the fill layer (for the polygon
// interior) and the outline layer (so each obec's border matches
// its own fill colour when the user has hidden the borders – any
// sub-pixel geometric drift between neighbours then becomes
// invisible because the line painted along the edge is the same
// colour as the fill on at least one side).
const FILL_GUESSED = "#22c55e";
const FILL_DEFAULT = "#cbd5e1";
const FILL_HOVER = "#94a3b8";
/** Peak mode: guessed summit marker fill. */
const PEAK_GUESSED = "#16a34a";
/** Peak mode: unguessed summit placeholder. */
const PEAK_DEFAULT = "#cbd5e1";
/** Revealed-but-missed PoIs (all point/line modes except metro). */
const POI_MISSED = "#dc2626";
const POI_MISSED_STROKE = "#991b1b";

/** Visible inter-obec border colour when "Hranice obcí" is on. */
const BORDER_COLOR_ON = "#94a3b8";

/** Subtle Czechia background fill/outline for sparse game modes. */
const COUNTRY_FILL = "#e2e8f0";
const COUNTRY_FILL_OPACITY = 0.35;
const COUNTRY_OUTLINE = "#cbd5e1";

function guessedFillExpression(
  categoryProperty?: string,
  categoryColors?: Record<string, { fill: string; outline: string }>,
): maplibregl.ExpressionSpecification {
  if (categoryProperty && categoryColors) {
    const pairs = Object.entries(categoryColors).flatMap(([key, colors]) => [
      key,
      colors.fill,
    ]);
    return [
      "case",
      ["boolean", ["feature-state", "guessed"], false],
      ["match", ["get", categoryProperty], ...pairs, FILL_GUESSED],
      ["boolean", ["feature-state", "hover"], false],
      FILL_HOVER,
      FILL_DEFAULT,
    ] as unknown as maplibregl.ExpressionSpecification;
  }
  return [
    "case",
    ["boolean", ["feature-state", "guessed"], false],
    FILL_GUESSED,
    ["boolean", ["feature-state", "hover"], false],
    FILL_HOVER,
    FILL_DEFAULT,
  ];
}

function guessedOutlineExpression(
  categoryProperty?: string,
  categoryColors?: Record<string, { fill: string; outline: string }>,
): maplibregl.ExpressionSpecification | string {
  if (categoryProperty && categoryColors) {
    const pairs = Object.entries(categoryColors).flatMap(([key, colors]) => [
      key,
      colors.outline,
    ]);
    return [
      "match",
      ["get", categoryProperty],
      ...pairs,
      "#166534",
    ] as unknown as maplibregl.ExpressionSpecification;
  }
  return "#166534";
}

function borderColorOffExpression(
  categoryProperty?: string,
  categoryColors?: Record<string, { fill: string; outline: string }>,
): maplibregl.ExpressionSpecification {
  if (categoryProperty && categoryColors) {
    const pairs = Object.entries(categoryColors).flatMap(([key, colors]) => [
      key,
      colors.fill,
    ]);
    return [
      "case",
      ["boolean", ["feature-state", "guessed"], false],
      ["match", ["get", categoryProperty], ...pairs, FILL_GUESSED],
      FILL_DEFAULT,
    ] as unknown as maplibregl.ExpressionSpecification;
  }
  return [
    "case",
    ["boolean", ["feature-state", "guessed"], false],
    FILL_GUESSED,
    FILL_DEFAULT,
  ];
}

/** Bounding box of Czechia (approx) used as initial camera. */
const CZ_BOUNDS: BBox = [12.09, 48.55, 18.86, 51.06];

/**
 * Colour expression that maps a feature's `line1` / `line2` property
 * to its real-world metro line colour. Used twice: once for the
 * circle fill (the station's primary line) and once for the stroke
 * (transfer stations get an outer ring in the secondary line's
 * colour; single-line stations get a neutral dark border).
 */
function metroColorMatch(
  property: "line1" | "line2",
  fallback: string,
): maplibregl.ExpressionSpecification {
  return [
    "match",
    ["get", property],
    "A", METRO_LINE_COLORS.A,
    "B", METRO_LINE_COLORS.B,
    "C", METRO_LINE_COLORS.C,
    fallback,
  ];
}

/** Guessed → `guessedColor`; revealed-but-missed → red; else default. */
function poiColorExpression(
  guessedColor: string | maplibregl.ExpressionSpecification,
  defaultColor: string,
): maplibregl.ExpressionSpecification {
  return [
    "case",
    ["boolean", ["feature-state", "guessed"], false],
    guessedColor,
    ["boolean", ["feature-state", "reveal"], false],
    POI_MISSED,
    defaultColor,
  ];
}

/**
 * Pure SVG background — no tile basemap. Cleaner look and zero
 * external requests / API keys. We don't define `glyphs` because
 * there are no symbol/label layers; MapLibre rejects `undefined`
 * for that field if present.
 */
const STYLE: StyleSpecification = {
  version: 8,
  sources: {},
  layers: [
    {
      id: "background",
      type: "background",
      paint: { "background-color": "rgba(0,0,0,0)" },
    },
  ],
};

interface HoverInfo {
  id: string;
  /**
   * Populated when the entity is guessed, OR when the run has been
   * finished (so the user can see what they missed). Otherwise null,
   * to keep the tooltip from spoiling answers mid-game.
   */
  name: string | null;
  /** Sub-line text for polygon modes (e.g. "okres Náchod"). */
  sub: string | null;
  /** Metro mode: line letters this station belongs to ("AB", "C", …). */
  lines: string | null;
  guessed: boolean;
  x: number;
  y: number;
}

export interface MapViewProps {
  /** URL of the simplified GeoJSON polygons (or points) to render. */
  geojsonUrl: string;
  guesses: Set<string>;
  flyTo: { bbox: BBox; key: number } | null;
  bordersVisible: boolean;
  /**
   * If true, hover tooltips reveal the name of *un-guessed* features
   * too. Set when the user has ended the run.
   */
  revealAll: boolean;
  /**
   * Geometry kind of the dataset. Defaults to "polygon" (the
   * historical behaviour); pass "point" for metro-style modes that
   * render circle markers instead of fills.
   */
  geometryKind?: GeometryKind;
  /** Circle styling when `geometryKind` is `"point"`. */
  pointVariant?: "metro" | "peak";
  /**
   * Initial camera frame. Defaults to all of Czechia. Per-mode
   * datasets pass their pre-computed bounds so e.g. metro mode opens
   * already centred on Prague.
   */
  initialBounds?: BBox;
  /** GeoJSON property used with `categoryColors` for per-kind tints. */
  categoryProperty?: string;
  categoryColors?: Record<string, { fill: string; outline: string }>;
  /** When true, draw a simplified Czechia outline under game entities. */
  countryBackground?: boolean;
}

export function MapView({
  geojsonUrl,
  guesses,
  flyTo,
  bordersVisible,
  revealAll,
  geometryKind = "polygon",
  pointVariant = "metro",
  initialBounds,
  categoryProperty,
  categoryColors,
  countryBackground = false,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const styleLoadedRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const guessesRef = useRef(guesses);
  guessesRef.current = guesses;
  // Prevent redundant `fitBounds` animations when React re-renders /
  // effects are invoked more than once in development.
  const lastAutoFitBoundsKeyRef = useRef<string | null>(null);
  // Snapshot of `bordersVisible` for use inside the one-shot `load`
  // handler, so we paint the outline correctly on first render
  // without waiting for the subsequent effect.
  const bordersVisibleRef = useRef(bordersVisible);
  bordersVisibleRef.current = bordersVisible;
  // Same trick for revealAll: the mousemove handler is attached
  // inside the `load` callback and reads the live prop value via
  // this ref, so we don't have to re-attach listeners on every change.
  const revealAllRef = useRef(revealAll);
  revealAllRef.current = revealAll;
  const categoryPropertyRef = useRef(categoryProperty);
  categoryPropertyRef.current = categoryProperty;
  const categoryColorsRef = useRef(categoryColors);
  categoryColorsRef.current = categoryColors;
  const pointVariantRef = useRef(pointVariant);
  pointVariantRef.current = pointVariant;
  /** Stable ids from the loaded GeoJSON – used to sync feature-state. */
  const featureIdsRef = useRef<(string | number)[]>([]);

  // ------------------------------------------------------------------
  //  Initialise the map exactly once
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      bounds: initialBounds ?? CZ_BOUNDS,
      fitBoundsOptions: { padding: 20 },
      attributionControl: false,
      //attributionControl: { compact: true, customAttribution: "© ČÚZK – RÚIAN" },
      // No tile basemap: high-DPI is irrelevant, keep it light.
      renderWorldCopies: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    map.on("load", async () => {
      styleLoadedRef.current = true;
      // Load data lazily once the map is live. Keeping the fetch
      // here (rather than at app boot) lets the index file load and
      // first paint happen earlier — the geojson is the heavy one.
      try {
        const entityPromise = fetch(geojsonUrl);
        const countryPromise = countryBackground
          ? fetch(COUNTRY_GEOJSON_URL)
          : Promise.resolve(null);
        const [entityRes, countryRes] = await Promise.all([entityPromise, countryPromise]);
        if (!entityRes.ok) throw new Error(`HTTP ${entityRes.status}`);
        const data = await entityRes.json();
        if (!mapRef.current) return; // unmounted

        if (countryBackground && countryRes) {
          if (!countryRes.ok) throw new Error(`HTTP ${countryRes.status}`);
          const countryData = await countryRes.json();
          if (!mapRef.current) return;
          map.addSource(COUNTRY_SOURCE_ID, {
            type: "geojson",
            data: countryData,
            tolerance: 0.5,
          });
          map.addLayer({
            id: COUNTRY_FILL_LAYER,
            type: "fill",
            source: COUNTRY_SOURCE_ID,
            paint: {
              "fill-color": COUNTRY_FILL,
              "fill-opacity": COUNTRY_FILL_OPACITY,
            },
          });
          map.addLayer({
            id: COUNTRY_OUTLINE_LAYER,
            type: "line",
            source: COUNTRY_SOURCE_ID,
            paint: {
              "line-color": COUNTRY_OUTLINE,
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                5, 0.8,
                9, 1.2,
                11, 1.6,
              ],
            },
          });
        }

        map.addSource(SOURCE_ID, {
          type: "geojson",
          data,
          // MapLibre's internal tile format only accepts integer
          // feature ids. String ids that aren't numeric (e.g. river
          // wikidata codes like "Q131574") are silently dropped,
          // which breaks setFeatureState and makes hover/guess ids
          // disagree. promoteId tells MapLibre to use our stable
          // properties.id as the feature-state key instead.
          promoteId: "id",
          generateId: false,
          tolerance: 0.5,
        });

        // The branches share map init, hover plumbing, and
        // feature-state handling; only the layer set + hit-test
        // target layer differ. Keeping both in one component avoids
        // duplicating the lifecycle/listener glue.
        const hitLayer =
          geometryKind === "point"
            ? POINT_LAYER
            : geometryKind === "line"
              ? LINE_HIT_LAYER
              : FILL_LAYER;

        if (geometryKind === "line") {
          // Rivers: thin coloured line for the actual draw + a much
          // wider invisible layer on top of it for hit-testing. The
          // hit layer is needed because a 2–4 px river is brutal to
          // hover precisely; the invisible 12 px overlay catches the
          // mouse and lets MapLibre's queryRenderedFeatures hand us
          // the underlying feature.
          const guessedOrRevealed: maplibregl.ExpressionSpecification = [
            "any",
            ["boolean", ["feature-state", "guessed"], false],
            ["boolean", ["feature-state", "reveal"], false],
          ];

          map.addLayer({
            id: LINE_LAYER,
            type: "line",
            source: SOURCE_ID,
            layout: { "line-cap": "round", "line-join": "round" },
            paint: {
              "line-color": poiColorExpression(LINE_GUESSED, LINE_DEFAULT),
              "line-opacity": [
                "case",
                ["boolean", ["feature-state", "guessed"], false],
                1,
                ["boolean", ["feature-state", "reveal"], false],
                0.7,
                // Faint by default so unguessed rivers act as
                // background context, not loud puzzle answers.
                0.45,
              ],
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                5, ["case", guessedOrRevealed, 1.8, 1.0],
                8, ["case", guessedOrRevealed, 2.6, 1.4],
                11, ["case", guessedOrRevealed, 3.6, 1.8],
                14, ["case", guessedOrRevealed, 5.0, 2.4],
              ],
            },
          });
          map.addLayer({
            id: LINE_HIT_LAYER,
            type: "line",
            source: SOURCE_ID,
            paint: {
              "line-color": "#000",
              "line-opacity": 0,
              "line-width": 14,
            },
          });
        } else if (geometryKind === "point") {
          const isPeak = pointVariantRef.current === "peak";
          // Metro: line-coloured circles with transfer rings.
          // Peaks: neutral grey placeholders → green when guessed.
          const guessedOrRevealed: maplibregl.ExpressionSpecification = [
            "any",
            ["boolean", ["feature-state", "guessed"], false],
            ["boolean", ["feature-state", "reveal"], false],
          ];
          const isTransfer: maplibregl.ExpressionSpecification = [
            "!=",
            ["get", "line2"],
            "",
          ];

          map.addLayer({
            id: POINT_LAYER,
            type: "circle",
            source: SOURCE_ID,
            paint: {
              "circle-color": isPeak
                ? poiColorExpression(PEAK_GUESSED, PEAK_DEFAULT)
                : [
                    "case",
                    guessedOrRevealed,
                    metroColorMatch("line1", "#888"),
                    "#cbd5e1",
                  ],
              "circle-stroke-color": isPeak
                ? [
                    "case",
                    ["boolean", ["feature-state", "guessed"], false],
                    "#14532d",
                    ["boolean", ["feature-state", "reveal"], false],
                    POI_MISSED_STROKE,
                    "#64748b",
                  ]
                : [
                    "case",
                    ["all", guessedOrRevealed, isTransfer],
                    metroColorMatch("line2", "#222"),
                    guessedOrRevealed,
                    "#0f172a",
                    "#64748b",
                  ],
              "circle-radius": [
                "interpolate", ["linear"], ["zoom"],
                10, isPeak ? 5 : 4,
                12, isPeak ? 7 : 6,
                14, isPeak ? 10 : 9,
                16, isPeak ? 13 : 12,
              ],
              "circle-stroke-width": isPeak
                ? [
                    "case",
                    guessedOrRevealed,
                    1.5,
                    1,
                  ]
                : [
                    "case",
                    ["all", guessedOrRevealed, isTransfer],
                    3,
                    guessedOrRevealed,
                    1.5,
                    1,
                  ],
              "circle-opacity": [
                "case",
                ["boolean", ["feature-state", "guessed"], false],
                1,
                ["boolean", ["feature-state", "reveal"], false],
                0.65,
                0.75,
              ],
              "circle-stroke-opacity": [
                "case",
                ["boolean", ["feature-state", "guessed"], false],
                1,
                ["boolean", ["feature-state", "reveal"], false],
                0.65,
                0.75,
              ],
            },
          });
        } else {
          const catProp = categoryPropertyRef.current;
          const catColors = categoryColorsRef.current;
          map.addLayer({
            id: FILL_LAYER,
            type: "fill",
            source: SOURCE_ID,
            paint: {
              "fill-color": guessedFillExpression(catProp, catColors),
              "fill-opacity": 1,
            },
          });
          map.addLayer({
            id: OUTLINE_LAYER,
            type: "line",
            source: SOURCE_ID,
            paint: {
              // Color is driven by the bordersVisible effect below;
              // pre-seed with the right one to avoid a flicker on load.
              "line-color": bordersVisibleRef.current
                ? BORDER_COLOR_ON
                : borderColorOffExpression(catProp, catColors),
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                5, 0.6,
                7, 0.9,
                9, 1.2,
                11, 1.6,
              ],
            },
          });
          // `feature-state` is not allowed inside `filter`, only in
          // `paint`. We always render this layer for all features and
          // drive per-feature visibility via `line-opacity`.
          map.addLayer({
            id: GUESSED_OUTLINE_LAYER,
            type: "line",
            source: SOURCE_ID,
            paint: {
              "line-color": guessedOutlineExpression(catProp, catColors),
              "line-opacity": [
                "case",
                ["boolean", ["feature-state", "guessed"], false],
                1,
                0,
              ],
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                5, 0.6,
                7, 0.9,
                9, 1.2,
                11, 1.6,
              ],
            },
          });
        }

        featureIdsRef.current = ((data.features ?? []) as Array<{ id?: string | number }>)
          .map((f) => f.id)
          .filter((id): id is string | number => id != null);

        // Hover state for tooltip + (polygon mode only) subtle hover fill.
        let hoveredId: string | number | null = null;
        map.on("mousemove", hitLayer, (e) => {
          if (!e.features || e.features.length === 0) return;
          const f = e.features[0]!;
          const fid = f.id;
          if (fid == null) return;
          if (hoveredId !== null && hoveredId !== fid) {
            map.setFeatureState({ source: SOURCE_ID, id: hoveredId }, { hover: false });
          }
          hoveredId = fid;
          map.setFeatureState({ source: SOURCE_ID, id: fid }, { hover: true });
          map.getCanvas().style.cursor = "pointer";
          const p = f.properties ?? {};
          const idStr = String(fid);
          const guessed = guessesRef.current.has(idStr);
          const reveal = guessed || revealAllRef.current;
          const okresStr = p["okres"] != null ? String(p["okres"]) : "";
          const linesStr = p["lines"] != null ? String(p["lines"]) : "";
          const rangeStr = p["range"] != null ? String(p["range"]) : "";
          const elevNum =
            Number(p["prominence"]) || Number(p["elevation"]) || 0;
          const peakSub =
            reveal && rangeStr
              ? elevNum > 0
                ? `${rangeStr} · ${elevNum} m`
                : rangeStr
              : null;
          setHover({
            id: idStr,
            name: reveal ? String(p["name"] ?? "") : null,
            sub: peakSub ?? (reveal && okresStr ? `okres ${okresStr}` : null),
            lines: reveal && linesStr ? linesStr : null,
            guessed,
            x: e.point.x,
            y: e.point.y,
          });
        });
        map.on("mouseleave", hitLayer, () => {
          if (hoveredId !== null) {
            map.setFeatureState({ source: SOURCE_ID, id: hoveredId }, { hover: false });
            hoveredId = null;
          }
          map.getCanvas().style.cursor = "";
          setHover(null);
        });
        setLoaded(true);
      } catch (err) {
        console.error(`Failed to load ${geojsonUrl}`, err);
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
      styleLoadedRef.current = false;
      featureIdsRef.current = [];
    };
    // We intentionally never re-run this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  //  Sync guesses → feature state
  //
  //  Full resync (not a diff) so a reset that lands while the map is
  //  still loading can't leave stale `guessed` flags behind.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    for (const id of featureIdsRef.current) {
      map.setFeatureState(
        { source: SOURCE_ID, id },
        { guessed: guesses.has(String(id)) },
      );
    }
  }, [guesses, loaded]);

  // ------------------------------------------------------------------
  //  Reveal toggle (non-polygon modes)
  //
  //  When the user finishes the run we want un-guessed metro
  //  stations / rivers to fade in so the player can see what they
  //  missed. The relevant paint expression checks the `reveal`
  //  feature-state, so we mirror `revealAll` onto every feature.
  //
  //  Applied only via this effect (not in the async map-load handler)
  //  so `revealAll` and the map are always in sync – otherwise a
  //  reset right after open can leave every PoI looking guessed.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || geometryKind === "polygon") return;
    for (const id of featureIdsRef.current) {
      map.setFeatureState({ source: SOURCE_ID, id }, { reveal: revealAll });
    }
  }, [revealAll, loaded, geometryKind]);

  // ------------------------------------------------------------------
  //  Explicit flyTo (only when the parent decides – e.g. user clicks
  //  a row in the sidebar). Never triggered automatically on guess.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !flyTo) return;
    const [w, s, e, n] = flyTo.bbox;
    map.fitBounds(
      [
        [w, s],
        [e, n],
      ],
      {
        padding: 80,
        // Polygons span enough area that zoom 11 is plenty; a single
        // metro station is a degenerate bbox so we need to zoom in
        // much further to make the marker visible at the centre.
        maxZoom: geometryKind === "point" ? 15 : 11,
        duration: 900,
        essential: true,
      },
    );
  }, [flyTo, loaded, geometryKind]);

  // ------------------------------------------------------------------
  //  Mode switch recentering
  //
  //  `App` remounts `MapView` on mode change, but it may pass
  //  `initialBounds={undefined}` until the new dataset finishes
  //  loading. Without this effect, the map would stick to the default
  // Czechia-wide bounds instead of re-centering to the mode area.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !initialBounds) return;
    const boundsKey = initialBounds.join(",");
    if (lastAutoFitBoundsKeyRef.current === boundsKey) return;
    lastAutoFitBoundsKeyRef.current = boundsKey;

    const [w, s, e, n] = initialBounds;
    map.fitBounds(
      [
        [w, s],
        [e, n],
      ],
      {
        // Keep the initial zoom comfortable so the user sees context.
        padding: 20,
        maxZoom: geometryKind === "point" ? 15 : 11,
        duration: 900,
        essential: true,
      },
    );
  }, [initialBounds, loaded, geometryKind]);

  // ------------------------------------------------------------------
  //  Borders toggle
  //  Rather than hiding the outline layer (which exposes sub-pixel
  //  drift between adjacent polygons as map-background slivers), we
  //  keep the line layer rendering and just repaint each border in
  //  the same colour as the obec it belongs to — so any visible
  //  artefact along the shared edge becomes a same-coloured stripe
  //  and disappears into the fill.
  // ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !map.getLayer(OUTLINE_LAYER)) return;
    map.setPaintProperty(
      OUTLINE_LAYER,
      "line-color",
      bordersVisible
        ? BORDER_COLOR_ON
        : borderColorOffExpression(
            categoryPropertyRef.current,
            categoryColorsRef.current,
          ),
    );
  }, [bordersVisible, loaded]);

  return (
    <div className="map-wrap">
      <div ref={containerRef} className="map" />
      {!loaded && <div className="map-loading">Načítám mapu…</div>}
      {hover && (
        <div className="tooltip" style={{ left: hover.x, top: hover.y }}>
          {hover.name ? (
            <>
              <div className={hover.guessed ? "t-name t-guessed" : "t-name t-missed"}>
                {hover.guessed ? "✓ " : "✗ "}
                {hover.name}
              </div>
              {hover.lines && (
                <div className="t-sub t-lines">
                  {[...hover.lines].map((c) => (
                    <span
                      key={c}
                      className="line-chip"
                      style={{ background: METRO_LINE_COLORS[c as "A" | "B" | "C"] ?? "#888" }}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}
              {hover.sub && <div className="t-sub">{hover.sub}</div>}
            </>
          ) : (
            <div className="t-sub">???</div>
          )}
        </div>
      )}
    </div>
  );
}
